import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider } from './hooks/useAuth';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AdminRoute } from './components/auth/AdminRoute';
import { InternalRoute } from './components/auth/InternalRoute';
import { AppLayout } from './components/layout/AppLayout';
import { Login } from './pages/auth/Login';
import { TwoFactor } from './pages/auth/TwoFactor';
import { TotpSetup } from './pages/auth/TotpSetup';
import { Dashboard } from './pages/dashboard/Dashboard';
import { TicketList } from './pages/tickets/TicketList';
import { TicketDetail } from './pages/tickets/TicketDetail';
import { TicketTypeSelector } from './pages/tickets/TicketTypeSelector';
import { OpenOrderWizard } from './pages/tickets/wizards/OpenOrderWizard';
import { AddressWizard } from './pages/tickets/wizards/AddressWizard';
import { SupportWizard } from './pages/tickets/wizards/SupportWizard';
import { BuildWizard } from './pages/tickets/wizards/BuildWizard';
import { HealthCheck } from './pages/HealthCheck';
import { UserGuidePage } from './pages/help/UserGuidePage';
import { SlaConfigPage } from './pages/admin/SlaConfigPage';
import { OperatorSettings } from './pages/admin/OperatorSettings';
import { UserManagement } from './pages/admin/UserManagement';
import { ApiKeys } from './pages/admin/ApiKeys';
import { AuditLog } from './pages/admin/AuditLog';
import { GroupManagement } from './pages/admin/GroupManagement';
import { EmailTemplates } from './pages/admin/EmailTemplates';
import { EmailQueue } from './pages/admin/EmailQueue';
import { AssignmentRoutingPage } from './pages/admin/AssignmentRoutingPage';
import { OrderingConfigPage } from './pages/admin/OrderingConfigPage';
import { PrivacyRequestsPage } from './pages/admin/PrivacyRequestsPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { NotificationsPage } from './pages/notifications/NotificationsPage';
import { ChangePasswordPage } from './pages/profile/ChangePasswordPage';
import { ForgotPassword } from './pages/auth/ForgotPassword';
import { ResetPassword } from './pages/auth/ResetPassword';
import { ForceChangePassword } from './pages/auth/ForceChangePassword';
import { AddressInfoPage } from './pages/AddressInfoPage';
import { ReportingPage } from './pages/reporting/ReportingPage';
import { PrivacyNoticePage } from './pages/legal/PrivacyNoticePage';

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster richColors position="top-right" />
        <Routes>
          {/* Standalone pages (no auth required) */}
          <Route path="/login" element={<Login />} />
          <Route path="/2fa" element={<TwoFactor />} />
          <Route path="/totp-setup" element={<TotpSetup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/force-change-password" element={<ForceChangePassword />} />
          <Route path="/privacy" element={<PrivacyNoticePage />} />

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/tickets" element={<TicketList />} />
              <Route path="/tickets/:id" element={<TicketDetail />} />
              <Route path="/tickets/new" element={<TicketTypeSelector />} />
              <Route path="/tickets/new/open-order" element={<OpenOrderWizard />} />
              <Route path="/tickets/new/address" element={<AddressWizard />} />
              <Route path="/tickets/new/support" element={<SupportWizard />} />
              <Route path="/tickets/new/build" element={<BuildWizard />} />
              <Route element={<AdminRoute />}>
                <Route path="/admin/sla-configs" element={<SlaConfigPage />} />
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/admin/operators" element={<OperatorSettings />} />
                <Route path="/admin/groups" element={<GroupManagement />} />
                <Route path="/admin/api-keys" element={<ApiKeys />} />
                <Route path="/admin/audit-log" element={<AuditLog />} />
                <Route path="/admin/email-templates" element={<EmailTemplates />} />
                <Route path="/admin/email-queue" element={<EmailQueue />} />
                <Route path="/admin/assignment-routing" element={<AssignmentRoutingPage />} />
                <Route path="/admin/ordering-config" element={<OrderingConfigPage />} />
                <Route path="/admin/privacy" element={<PrivacyRequestsPage />} />
              </Route>
              <Route element={<InternalRoute />}>
                <Route path="/reporting" element={<ReportingPage />} />
              </Route>
              <Route path="/help" element={<UserGuidePage />} />
              <Route path="/address-information" element={<AddressInfoPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/profile/change-password" element={<ChangePasswordPage />} />
              <Route path="/health" element={<HealthCheck />} />
            </Route>
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
